/**
 * PortalLayout — lightweight layout for the parent portal.
 * Distinct from the admin MainLayout — no sidebar, simplified header.
 */

import React from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@core/auth/AuthContext';
import styled from 'styled-components';

// ---------------------------------------------------------------------------
// Styles (inline to keep the portal self-contained)
// ---------------------------------------------------------------------------

const Wrapper = styled.div`
  min-height: 100svh;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme?.colors?.background ?? '#f9fafb'};
`;

const Header = styled.header`
  background: #6366f1;
  color: #fff;
  padding: 0 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  position: sticky;
  top: 0;
  z-index: 50;
`;

const Brand = styled(Link)`
  color: #fff;
  font-weight: 700;
  font-size: 1.125rem;
  text-decoration: none;
`;

const Nav = styled.nav`
  display: flex;
  gap: 1rem;
  a {
    color: rgba(255,255,255,.85);
    text-decoration: none;
    font-size: 0.875rem;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    &.active { background: rgba(255,255,255,.15); color: #fff; }
    &:hover   { background: rgba(255,255,255,.10); }
  }
`;

const SignoutBtn = styled.button`
  background: rgba(255,255,255,.15);
  color: #fff;
  border: 1px solid rgba(255,255,255,.3);
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8125rem;
  &:hover { background: rgba(255,255,255,.25); }
`;

const Main = styled.main`
  flex: 1;
  max-width: 900px;
  width: 100%;
  margin: 0 auto;
  padding: 1.5rem 1rem;
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PortalLayout: React.FC = () => {
  const { signOut, user } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <Wrapper data-testid="candeza-portal-layout">
      <Header>
        <Brand to="/portal" data-testid="candeza-portal-brand">
          My Portal
        </Brand>
        <Nav data-testid="candeza-portal-nav">
          <NavLink to="/portal/students"  data-testid="candeza-portal-nav-students">Students</NavLink>
          <NavLink to="/portal/sessions"  data-testid="candeza-portal-nav-sessions">Sessions</NavLink>
          <NavLink to="/portal/invoices"  data-testid="candeza-portal-nav-invoices">Invoices</NavLink>
          <NavLink to="/portal/balance"   data-testid="candeza-portal-nav-balance">Balance</NavLink>
          <NavLink to="/portal/grades"    data-testid="candeza-portal-nav-grades">Grades</NavLink>
          <NavLink to="/portal/requests"  data-testid="candeza-portal-nav-requests">Requests</NavLink>
        </Nav>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {user && (
            <span style={{ fontSize: '0.8125rem', opacity: 0.85 }}>
              {user.email ?? user.uid.slice(0, 8)}
            </span>
          )}
          <SignoutBtn onClick={handleSignOut} data-testid="candeza-portal-signout-btn">
            Sign out
          </SignoutBtn>
        </div>
      </Header>
      <Main>
        <Outlet />
      </Main>
    </Wrapper>
  );
};

export default PortalLayout;

